import { ArgumentException } from "@/lib/exceptions/ArgumentException";
import { Utils } from "@/lib/utility/utils";
import { FormEntity } from "@/models/formEntity";
import { PartnerContactEntity } from "@/models/partnerContactEntity";
import { PartnerEntity } from "@/models/partnerEntity";
import { RatingEntity } from "@/models/ratingEntitity";
import { RegistrationResultsEntity } from "@/models/registrationResultsEntity";
import { ReportEntity } from "@/models/reportEntity";
import { MrfiktivHttpClientProvider as Api } from "@/services/mrfiktiv/mrfiktiv-http-client.provider";
import { IPartnerUpdateDto } from "@/store/interface/partner-update.dto.interface";
import { IUpdateProgressStatusDto } from "@/store/interface/update-progress-status-dto.interface";
import { AxiosInstance } from "axios";
import { MrfiktivCreatePartnerDtoGen } from "../v1/data-contracts";

/**
 * The service to communicate with the partner backend.
 */
class PartnerService {
  ROUTE = "partner";
  /**
   * The axios instance.
   */
  api: AxiosInstance;

  /**
   * @class Initialize PartnerService.
   */
  constructor(axiosInstance: AxiosInstance) {
    if (!axiosInstance) throw new ArgumentException(axiosInstance);
    this.api = axiosInstance;
  }

  async contact(partnerContact: PartnerContactEntity, partnerName: string): Promise<any> {
    const formData = new FormData();
    Utils.buildFormData(formData, partnerContact);

    const config = {
      headers: {
        "content-type": "multipart/form-data"
      }
    };

    // FIXME: This needs to be fixed because of the THG logic :(
    this.api = new Api().instance();
    return await this.api.post(`/${this.ROUTE}/${partnerName}/contact`, formData, config);
  }

  async getPartnerByName(partnerName: string): Promise<any> {
    // FIXME: This needs to be fixed because of the THG logic :(
    this.api = new Api().instance();

    return await this.api.get(`/partner/byName?partnerName=${partnerName}`);
  }

  async getPartnerByHost(host: string): Promise<any> {
    // FIXME: This needs to be fixed because of the THG logic :(
    this.api = new Api().instance();

    return await this.api.get(`/partner/byUrl?url=${host}`);
  }

  async addRatingForPartner(partnerId: string, rating: RatingEntity): Promise<any> {
    // FIXME: This needs to be fixed because of the THG logic :(
    this.api = new Api().instance();
    return await this.api.post(`/partner/${partnerId}/rating`, rating);
  }

  async getPartnerById(partnerId: string): Promise<any> {
    // FIXME: This needs to be fixed because of the THG logic :(
    this.api = new Api().instance();
    return await this.api.get(`/partner/${partnerId}`);
  }

  async getAllPartners(): Promise<PartnerEntity[]> {
    // FIXME: This needs to be fixed because of the THG logic :(
    this.api = new Api().instance();
    const response = await this.api.get("/partner");

    const partnerList: PartnerEntity[] = [];
    for (let i = 0; i < response.data.length; i++) {
      partnerList.push(response.data[i]);
    }

    return partnerList;
  }

  async create(partner: MrfiktivCreatePartnerDtoGen): Promise<PartnerEntity> {
    // FIXME: This needs to be fixed because of the THG logic :(
    this.api = new Api().instance();
    const response = await this.api.post("/partner", partner);
    return response.data;
  }

  async updatePartner(partnerUpdateDto: IPartnerUpdateDto): Promise<any> {
    // FIXME: This needs to be fixed because of the THG logic :(
    this.api = new Api().instance();
    return await this.api.patch(`/partner/${partnerUpdateDto.id}`, partnerUpdateDto.partnerUpdateEntity);
  }

  async deletePartner(partnerId: string): Promise<any> {
    // FIXME: This needs to be fixed because of the THG logic :(
    this.api = new Api().instance();
    return await this.api.delete(`/partner/${partnerId}`);
  }

  // ### FORM ###

  async getAllFormsForPartner(partnerId: string): Promise<FormEntity[]> {
    // FIXME: This needs to be fixed because of the THG logic :(
    this.api = new Api().instance();
    const response = await this.api.get(`/partner/${partnerId}/contact`);
    return response.data as FormEntity[];
  }

  async getFormById(partnerId: string, formId: string): Promise<any> {
    // FIXME: This needs to be fixed because of the THG logic :(
    this.api = new Api().instance();
    const response = await this.api.get(`/partner/${partnerId}/contact/${formId}`);
    return response.data as FormEntity;
  }

  async getExportForFormById(partnerId: string, formId: string, exportType: string): Promise<any> {
    // FIXME: This needs to be fixed because of the THG logic :(
    this.api = new Api().instance();
    const response = await this.api.get(`/partner/${partnerId}/contact/${formId}/export/${exportType}`);
    return response;
  }

  // ### REPORT ###

  async getAllReportsForPartner(partnerId: string): Promise<ReportEntity[]> {
    // FIXME: This needs to be fixed because of the THG logic :(
    this.api = new Api().instance();
    const response = await this.api.get(`/partner/${partnerId}/report`);
    return response.data as ReportEntity[];
  }

  async getReportById(partnerId: string, reportId: string): Promise<ReportEntity> {
    // FIXME: This needs to be fixed because of the THG logic :(
    this.api = new Api().instance();
    const response = await this.api.get(`/partner/${partnerId}/report/${reportId}`);

    const r = response.data as ReportEntity;
    return {
      _id: r._id,
      customerAddress: {
        city: r.customerAddress.city,
        country: r.customerAddress.country,
        postalCode: r.customerAddress.postalCode,
        street: r.customerAddress.street
      },
      customerContact: {
        email: r.customerContact.email,
        phone: r.customerContact.phone
      },
      customerName: r.customerName,
      numberplate: r.numberplate,
      damage: r.damage,
      datePreference: r.datePreference,
      images: {
        registrations: r.images.registrations,
        cockpits: r.images.cockpits,
        damages: r.images.damages,
        overviews: r.images.overviews,
        damagesDetail: r.images.damagesDetail
      },
      marketingOptIn: r.marketingOptIn,
      message: r.message,
      partnerId: r.partnerId,
      registrationResults: r.registrationResults,
      timestamps: r.timestamps,
      progressStatus: r.progressStatus
    };
  }

  async getExportForReportById(partnerId: string, reportId: string, exportType: string): Promise<any> {
    // FIXME: This needs to be fixed because of the THG logic :(
    this.api = new Api().instance();
    const response = await this.api.get(`/partner/${partnerId}/report/${reportId}/export/${exportType}`);

    return response;
  }

  async addRegistrationDetailsForForm(
    partnerId: string,
    formId: string,
    registrationResult: RegistrationResultsEntity
  ): Promise<any> {
    // FIXME: This needs to be fixed because of the THG logic :(
    this.api = new Api().instance();
    const response = await this.api.post(`/partner/${partnerId}/contact/${formId}/registration`, registrationResult);
    return response.data as FormEntity;
  }

  async addRegistrationDetailsForReport(
    partnerId: string,
    reportId: string,
    registrationResult: RegistrationResultsEntity
  ): Promise<any> {
    // FIXME: This needs to be fixed because of the THG logic :(
    this.api = new Api().instance();
    const response = await this.api.post(`/partner/${partnerId}/report/${reportId}/registration`, registrationResult);
    return response.data as ReportEntity;
  }

  async updateProgressStatus(
    partnerId: string,
    reportId: string,
    updateProgressStatusDto: IUpdateProgressStatusDto
  ): Promise<ReportEntity> {
    // FIXME: This needs to be fixed because of the THG logic :(
    this.api = new Api().instance();
    const response = await this.api.patch(`/partner/${partnerId}/report/${reportId}/status`, updateProgressStatusDto);
    return response.data as ReportEntity;
  }
}

export default new PartnerService(new Api().instance());
